import { notFound, redirect } from "@tanstack/react-router";
import { matchRoute } from "@softmax/web-addresses";
import { gameNavigationHref } from "../../game-types";
import { type GamePageContext } from "../../game-types";
import { roomFlagState } from "@/data/flags";
import { routeFlags } from "./route-access";
import { publicPresentationAdmission } from "./public-presentation";
import { unflaggedAdmission, signedInAdmission } from "./signed-in";
import { gameAliasAdmission } from "./game-alias";
import { type AdmissionDecision, type SiteAdmission } from "./admission.types";

export async function resolveAdmissionDecision({
  signal,
  href,
  game,
}: {
  signal?: AbortSignal | undefined;
  href: string;
  game: GamePageContext | null;
}): Promise<AdmissionDecision> {
  const requestUrl = new URL(window.location.href);

  if (!URL.canParse(href, requestUrl)) return { kind: "not-found" };

  const url = new URL(href, requestUrl);

  if (url.origin !== requestUrl.origin || url.username || url.password) {
    return { kind: "not-found" };
  }

  const publicAdmission = publicPresentationAdmission({
    url,
    game,
  });

  if (publicAdmission) return publicAdmission;

  const route = matchRoute(url.pathname);
  const featureKey = routeFlags.get(route.name);

  if (featureKey) {
    const flag = await roomFlagState({
      signal,
      key: featureKey,
    });

    if (flag === "unavailable") {
      return { kind: "unavailable", message: "Game access is unavailable" };
    }

    if (flag === "disabled")
      return unflaggedAdmission({
        signal,
        url,
        game,
      });
  }

  const alias = gameAliasAdmission({
    url,
    game,
  });

  if (alias) return alias;

  return signedInAdmission({
    signal,
    url,
    game,
  });
}

async function resolveSiteAdmission({
  signal,
  href,
  game,
}: {
  signal?: AbortSignal | undefined;
  href: string;
  game: GamePageContext | null;
}): Promise<SiteAdmission> {
  const admission = await resolveAdmissionDecision({
    signal,
    href,
    game,
  });

  if (admission.kind !== "navigate") return admission;

  return {
    kind: "redirect",
    href: gameNavigationHref({ navigation: admission.navigation, href }),
    status:
      admission.navigation.kind === "path" ? admission.navigation.status : 307,
  };
}

export async function requireSiteAdmission({
  signal,
  href,
  game,
}: {
  signal?: AbortSignal | undefined;
  href: string;
  game: GamePageContext | null;
}): Promise<
  Extract<
    SiteAdmission,
    {
      kind: "allowed";
    }
  >
> {
  const admission = await resolveSiteAdmission({
    signal,
    href,
    game,
  });

  if (admission.kind === "redirect") {
    throw redirect({ href: admission.href, statusCode: admission.status });
  }

  if (admission.kind === "not-found") throw notFound();

  if (admission.kind === "unavailable") {
    throw new Error(admission.message);
  }

  return admission;
}
